import { useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import Prestation from "./prestation";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  addSelectedPrestation,
  clearSelectedPrestations,
} from "../store/slices/rendezVousSlice";
import { setSelectedEtablissement } from "../store/slices/etablissementSlice";
import CustomSelect from "./inputs/select";

const EtablissementServices = ({
  services,
  horaires,
  disponibilites = [],
  status,
  etablissement,
}) => {
  const [selectedService, setSelectedService] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [how, setHow] = useState(null);
  const [filteredPrestations, setFilteredPrestations] = useState(null);
  const sliderRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [maxPosition, setMaxPosition] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize default selected service and max position
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const serviceId = params.get('service');
    
    if (services?.length > 0) {
      let serviceToSelect;
      
      if (serviceId) {
        serviceToSelect = services.find(s => s.id === parseInt(serviceId));
      }
      
      if (!serviceToSelect) {
        serviceToSelect = services[0];
      }
      
      setSelectedService(serviceToSelect);
      if (how) applyFilter(serviceToSelect, how);
      calculateMaxPosition();
    }
  }, [services, location.search, how]);

  // Calculate max scroll position
  const calculateMaxPosition = () => {
    if (sliderRef.current) {
      const containerWidth = sliderRef.current.offsetWidth;
      const contentWidth = sliderRef.current.scrollWidth;
      setMaxPosition(contentWidth - containerWidth);
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      calculateMaxPosition();
      if (sliderRef.current) {
        sliderRef.current.scrollLeft = sliderPosition;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sliderPosition]);

  const applyFilter = (service, filterValue) => {
    if (!filterValue) {
      setFilteredPrestations(null);
      return;
    }

    const filtered = service.prestations.filter((prestation) => {
      if (filterValue === "") return true;
      return prestation.lieu?.includes(filterValue);
    });
    setFilteredPrestations(filtered);
  };

  const handleServiceChange = (service) => {
    setSelectedService(service);
    navigate(`?service=${service.id}`, {state: {idEtablissement: location?.state?.idEtablissement}});
    if (how) {
      applyFilter(service, how);
    } else {
      setFilteredPrestations(null);
    }
  };

  const filterData = (value) => {
    setHow(value);
    if (selectedService) {
      applyFilter(selectedService, value);
    }
  };

  const handleScroll = (direction) => {
    if (!sliderRef.current) return;

    const containerWidth = sliderRef.current.offsetWidth;
    const scrollAmount = containerWidth * 0.8;

    let newPosition;
  
    if (direction === 'next') {
      newPosition = Math.min(sliderPosition + scrollAmount, maxPosition);
      newPosition+=80
    } else {
      newPosition = Math.max(sliderPosition - scrollAmount, 0);
    }

    sliderRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth'
    });
    setSliderPosition(newPosition);
  };

  // Loading state
  if (status === "loading") {
    return (
      <div className="etablissement-services">
        <div className="single row">
          <div className="services-col col-md-12">
            <h3 className="grey500">
              <Skeleton width={200} />
            </h3>
            <div className="service-cards">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="service-card">
                  <Skeleton width={150} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!services || services.length === 0) {
    return <div className="etablissement-services">Aucun service disponible.</div>;
  }

  return (
    <div className="etablissement-services">
      <div className="single row">
        <div className="services-col col-md-12">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="grey500 mb-0">Réservez votre service</h3>
            </div>
            <div className="col-auto">
              <CustomSelect
                options={[
                  { label: "Tous", value: "" },
                  { label: "À domicile", value: "onsite" },
                  { label: "En ligne", value: "enligne" },
                  { label: "Chez le professionnel", value: "chezpro" },
                ]}
                placeholder="Filtrer"
                onChange={filterData}
                selectedValue={how}
                className="filterbtn"
                // width={"max-content"}
                icon={
                  <img src="/images/filter.svg" alt="Trier" className="mr-8" />
                }
              />
            </div>
          </div>

          <div className="service-slider-container d-flex align-items-center">
            <div 
              className="service-cards"
              ref={sliderRef}
              onScroll={(e) => setSliderPosition(e.target.scrollLeft)}
            >
              {services.map((service) => (
                <div
                  key={service.id}
                  className={`service-card ${
                    selectedService?.id === service.id ? "active" : ""
                  }`}
                  onClick={() => handleServiceChange(service)}
                >
                  {service.name}
                </div>
              ))}
            </div>

            <div className="slider-controls">
              <button
                onClick={() => handleScroll('prev')}
                className="link-blue"
                disabled={sliderPosition <= 0}
              >
                <img src="/images/left.svg" alt="Previous" />
              </button>
              <button
                onClick={() => handleScroll('next')}
                className="link-blue"
                disabled={sliderPosition >= maxPosition}
              >
                <img src="/images/left.svg" alt="Next" className="rotate180" />
              </button>
            </div>
          </div>

          {selectedService && (
            <div className="prestations mt-4">
              {selectedService.prestations?.length === 0 ? (
                <p className="body grey66">
                  Aucune prestation disponible pour ce service.
                </p>
              ) : (
                <>
                  {(filteredPrestations || selectedService.prestations)
                    ?.slice(0, showAll ? undefined : 5)
                    .map((prestation, index) => (
                      <div key={index} className="mb-3">
                        <Prestation
                          prestation={prestation}
                          classNameBtn="twyny-cta-secondary"
                          buttonName="Réserver"
                          buttonAction={() => {
                            navigate(`/details-rdv/${etablissement.id}`);
                            dispatch(clearSelectedPrestations());
                            dispatch(addSelectedPrestation(prestation));
                            dispatch(setSelectedEtablissement(etablissement));
                          }}
                        />
                      </div>
                    ))}
                  {selectedService.prestations?.length > 5 && (
                    <div className="text-center mt-3">
                      <button
                        className="link-blue"
                        onClick={() => setShowAll(!showAll)}
                      >
                        {showAll ? "Voir moins" : "Voir toutes les prestations"}
                        <br />
                        <img
                          src="/images/down-arrow.svg"
                          alt="down arrow icon"
                        />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EtablissementServices;